/**
 * Coups de pouce — Attendu 682 « Définition de la proportionnalité et expressions de la vie courante ».
 * Une entrée par generator_key (cf. gen_682.js).
 * ⚠ `definition_qcm` : PAS d'entrée (pur rappel, doctrine gen 640, décision
 * utilisateur 2026-08-09) — le FAB d'aide ne se monte pas.
 */

// ── Expressions : trouver les deux grandeurs cachées (id cp682play1) ──
// Exemple hors pool (« Nombre de mots lus par minute ») : rien de recopiable.
// Les deux morceaux de l'expression se surlignent tour à tour, chacun révèle
// sa grandeur. Cadence 2 s, remise à zéro au clic.
const SVG_EXPR_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 128" role="img"
     aria-label="L'expression Nombre de mots lus par minute. « Nombre de mots lus » se surligne : la première grandeur est un nombre de mots. Puis « par minute » : la deuxième grandeur est une durée">
  <g id="cp682play1" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <!-- surlignages derrière l'expression -->
  <rect x="52" y="48" width="160" height="24" rx="4" fill="#A7F3D0" opacity="0"><animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play1.click" fill="freeze"/><animate attributeName="opacity" values="0;0.7" dur="0.3s" begin="cp682play1.click+0.3s" fill="freeze"/></rect>
  <rect x="214" y="48" width="96" height="24" rx="4" fill="#FDE68A" opacity="0"><animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play1.click" fill="freeze"/><animate attributeName="opacity" values="0;0.7" dur="0.3s" begin="cp682play1.click+2.3s" fill="freeze"/></rect>
  <!-- l'expression -->
  <text x="56" y="65" font-size="15" font-weight="700" fill="#0f172a" font-family="system-ui,sans-serif">Nombre de mots lus <tspan>par minute</tspan></text>
  <!-- les deux grandeurs, en pastilles -->
  <g opacity="0">
    <rect x="20" y="88" width="8" height="8" rx="2" fill="#1D9E75"/>
    <text x="36" y="96" font-size="13" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">La première grandeur est un nombre de mots.</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play1.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.25s" begin="cp682play1.click+0.3s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="20" y="110" width="8" height="8" rx="2" fill="#B45309"/>
    <text x="36" y="118" font-size="13" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">La deuxième grandeur est une durée.</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play1.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.25s" begin="cp682play1.click+2.3s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_EXPRESSIONS = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'Chaque expression cache **deux grandeurs**.',
            ],
            svg: SVG_EXPR_ANIM,
        },
    ],
};

// ── V/F : le test de la multiplication (id cp682play2) ──
// Exemples hors pool (cahiers, sapin) : rien de recopiable — le contre-exemple
// n'est PAS âge/taille, qui est une ligne du V/F. Un cas proportionnel
// (toujours × 3) face à un cas qui ne l'est pas (× 0,5 puis × 0,2).
const SVG_TEST_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 200" role="img"
     aria-label="Deux situations. Prix des cahiers : une flèche fois 3 va de 2 cahiers vers 6 euros, une autre de 4 cahiers vers 12 euros, c'est proportionnel. Taille d'un sapin : une flèche fois 0,5 va de 2 ans vers 1 mètre, une flèche fois 0,2 de 20 ans vers 4 mètres, le nombre change, ce n'est pas proportionnel">
  <g id="cp682play2" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <!-- situation 1 : les cahiers -->
  <g opacity="0">
    <text x="20" y="48" font-size="12" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Prix des cahiers</text>
    <text x="20" y="94" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">2 cahiers</text>
    <text x="120" y="94" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">6 €</text>
    <text x="20" y="140" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">4 cahiers</text>
    <text x="120" y="140" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">12 €</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+0.3s" fill="freeze"/>
  </g>
  <!-- flèches × 3 au-dessus des textes -->
  <g opacity="0" stroke="#1D9E75" stroke-width="1.5" fill="none">
    <path d="M45 82 Q85 62 126 82"/>
    <path d="M126 82 L119 77 M126 82 L120 86"/>
    <path d="M45 128 Q85 108 126 128"/>
    <path d="M126 128 L119 123 M126 128 L120 132"/>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+1.1s" fill="freeze"/>
  </g>
  <g opacity="0" font-size="12" font-weight="700" fill="#1D9E75" text-anchor="middle" font-family="system-ui,sans-serif">
    <text x="85" y="62">× 3</text>
    <text x="85" y="108">× 3</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+1.1s" fill="freeze"/>
  </g>
  <!-- situation 2 : le sapin -->
  <g opacity="0">
    <text x="200" y="48" font-size="12" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">Taille d'un sapin</text>
    <text x="200" y="94" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">2 ans</text>
    <text x="292" y="94" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">1 m</text>
    <text x="200" y="140" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">20 ans</text>
    <text x="292" y="140" font-size="13" fill="#0f172a" font-family="system-ui,sans-serif">4 m</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+2.9s" fill="freeze"/>
  </g>
  <!-- flèches × 0,5 et × 0,2 au-dessus des textes -->
  <g opacity="0" stroke="#DC2626" stroke-width="1.5" fill="none">
    <path d="M218 82 Q258 62 298 82"/>
    <path d="M298 82 L291 77 M298 82 L292 86"/>
    <path d="M222 128 Q260 108 298 128"/>
    <path d="M298 128 L291 123 M298 128 L292 132"/>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+3.7s" fill="freeze"/>
  </g>
  <g opacity="0" font-size="12" font-weight="700" fill="#DC2626" text-anchor="middle" font-family="system-ui,sans-serif">
    <text x="258" y="62">× 0,5</text>
    <text x="258" y="108">× 0,2</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+3.7s" fill="freeze"/>
  </g>
  <!-- conclusions en pastilles -->
  <g opacity="0">
    <rect x="20" y="158" width="8" height="8" rx="2" fill="#1D9E75"/>
    <text x="36" y="166" font-size="13" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">Cahiers : toujours × 3, c'est proportionnel.</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+2.1s" fill="freeze"/>
  </g>
  <g opacity="0">
    <rect x="20" y="182" width="8" height="8" rx="2" fill="#DC2626"/>
    <text x="36" y="190" font-size="13" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">Sapin : le nombre change, ce n'est pas proportionnel.</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play2.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play2.click+4.6s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_PROPRIETES = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'Pour chaque phrase, demande-toi : passe-t-on de l\'une à l\'autre en **multipliant toujours par le même nombre** ?',
            ],
            svg: SVG_TEST_ANIM,
        },
    ],
};

// ── Saisie : multiplier la valeur unitaire (id cp682play3) ──
// Exemple hors pool (croissants) : rien de recopiable. Les deux lignes du
// raisonnement (1 croissant / 5 croissants) reliées par des flèches × 5 de
// part et d'autre, puis le « ? » devient 10 €.
const SVG_UNITE_ANIM = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150" role="img"
     aria-label="1 croissant coûte 2 euros. Pour 5 croissants, une flèche fois 5 relie 1 croissant à 5 croissants, une autre relie 2 euros au point d'interrogation, qui devient 10 euros">
  <g id="cp682play3" style="cursor:pointer">
    <title>Lancer l'animation</title>
    <rect x="266" y="4" width="86" height="24" rx="12" fill="#1E3A8A"/>
    <path d="M278 10 L278 22 L288 16 Z" fill="#ffffff"/>
    <text x="322" y="20" font-size="12" font-weight="600" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">Lancer</text>
  </g>
  <text x="8" y="20" font-size="13" font-weight="600" fill="#64748b" font-family="system-ui,sans-serif">1 croissant coûte 2 €</text>
  <!-- les deux lignes du raisonnement -->
  <g opacity="0">
    <text x="100" y="68" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">1 croissant</text>
    <text x="250" y="68" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">2 €</text>
    <text x="100" y="112" font-size="14" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">5 croissants</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+0.3s" fill="freeze"/>
  </g>
  <text x="250" y="112" font-size="14" font-weight="700" fill="#94a3b8" font-family="system-ui,sans-serif" opacity="0">?<animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/><animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+0.3s" fill="freeze"/><animate attributeName="opacity" values="1;0" dur="0.25s" begin="cp682play3.click+3.3s" fill="freeze"/></text>
  <text x="250" y="112" font-size="14" font-weight="700" fill="#1D9E75" font-family="system-ui,sans-serif" opacity="0">10 €<animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/><animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+3.4s" fill="freeze"/></text>
  <!-- flèche × 5 côté croissants -->
  <g opacity="0">
    <path d="M92 72 Q62 90 92 108" fill="none" stroke="#F97316" stroke-width="1.5"/>
    <path d="M92 108 L84 104 M92 108 L86 113" fill="none" stroke="#F97316" stroke-width="1.5"/>
    <text x="52" y="94" font-size="12" font-weight="700" fill="#F97316" text-anchor="middle" font-family="system-ui,sans-serif">× 5</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+1.3s" fill="freeze"/>
  </g>
  <!-- flèche × 5 côté prix -->
  <g opacity="0">
    <path d="M288 72 Q318 90 288 108" fill="none" stroke="#F97316" stroke-width="1.5"/>
    <path d="M288 108 L296 104 M288 108 L294 113" fill="none" stroke="#F97316" stroke-width="1.5"/>
    <text x="328" y="94" font-size="12" font-weight="700" fill="#F97316" text-anchor="middle" font-family="system-ui,sans-serif">× 5</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+2.3s" fill="freeze"/>
  </g>
  <!-- conclusion -->
  <g opacity="0">
    <rect x="20" y="132" width="8" height="8" rx="2" fill="#1E3A8A"/>
    <text x="36" y="140" font-size="13" font-weight="600" fill="#0f172a" font-family="system-ui,sans-serif">On multiplie le prix d'un croissant par 5.</text>
    <animate attributeName="opacity" values="0;0" dur="0.05s" begin="cp682play3.click" fill="freeze"/>
    <animate attributeName="opacity" values="0;1" dur="0.3s" begin="cp682play3.click+4.3s" fill="freeze"/>
  </g>
</svg>`.trim();

const CP_UNITE = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'L\'énoncé donne la valeur pour **une seule unité** (1 kg, 1 minute, 1 heure…). Multiplie-la par le nombre demandé.',
            ],
            svg: SVG_UNITE_ANIM,
        },
    ],
};

// ── Interprétation : remettre l'expression dans l'ordre « pour…, on a… » ──
// Entrée sobre, règle seule (décision utilisateur 2026-08-10) : l'exemple
// (tours par minute) est hors pool.
const CP_INTERPRETATION = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                'Remets l\'expression dans l\'ordre « **pour** …, **on a** … ».',
                '« 3 tours **par minute** » veut dire : **pour 1 minute**, on fait 3 tours.',
            ],
        },
    ],
};

const CP_682 = {
    expressions_relier: CP_EXPRESSIONS,
    proprietes_vf: CP_PROPRIETES,
    prix_unitaire_saisie: CP_UNITE,
    interpretation_qcm: CP_INTERPRETATION,
    // Partage (décision utilisateur 2026-08-10) : le test de la multiplication
    // répond aux variantes « laquelle est/n'est pas proportionnelle ? ».
    vocabulaire_qcm: CP_PROPRIETES,
};

export default CP_682;
